import React, { memo, useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { SecondaryButton, DeleteIconButton } from './components/AppButtons';

export type CommodityOption = {
  commodityID?: number | null;
  commodity?: string | null;
};

export type FinancialRow = {
  id: number;
  fy: string;
  value: string;
  period: string;
  commodityID?: number | null;
  commodity?: string;
  pov: boolean;
  pvo: boolean;
  supplierIFA?: string | null;
};

export const isParseSourcedFinancialRow = (row: FinancialRow): boolean =>
  String(row.supplierIFA ?? '').trim().length > 0;

const createEmptyFinancialRow = (): FinancialRow => ({
  id: Date.now(),
  fy: '',
  value: '',
  period: '',
  commodityID: null,
  commodity: '',
  pov: false,
  pvo: false,
  supplierIFA: null,
});

const getCommodityLabel = (option: CommodityOption | string) =>
  typeof option === 'string' ? option : option.commodity || '';

const getCommodityValue = (
  row: FinancialRow,
  commodityOptions: CommodityOption[] = []
): CommodityOption | string | null => {
  const resolvedName =
    row.commodity?.trim() ||
    (row.commodityID != null && row.commodityID > 0
      ? commodityOptions.find((option) => option.commodityID === row.commodityID)?.commodity?.trim() ?? ''
      : '');

  if (row.commodityID != null && row.commodityID > 0) {
    return { commodityID: row.commodityID, commodity: resolvedName };
  }
  if (resolvedName) {
    return resolvedName;
  }
  return null;
};

const isSameCommodityOption = (a: CommodityOption | string, b: CommodityOption | string) => {
  if (typeof a === 'string' || typeof b === 'string') {
    return getCommodityLabel(a).trim().toLowerCase() === getCommodityLabel(b).trim().toLowerCase();
  }
  if (a.commodityID != null && b.commodityID != null && a.commodityID > 0 && b.commodityID > 0) {
    return a.commodityID === b.commodityID;
  }
  return getCommodityLabel(a).trim().toLowerCase() === getCommodityLabel(b).trim().toLowerCase();
};

const readOnlyCellSx = {
  fontSize: 13,
  px: 0.5,
  py: 0.25,
  color: '#374151',
};

type FinancialRowItemProps = {
  row: FinancialRow;
  editMode: boolean;
  canDelete: boolean;
  parseDataOnly: boolean;
  showIfaColumn: boolean;
  commodityOptions: CommodityOption[];
  onCreateCommodity: (name: string) => Promise<CommodityOption | null>;
  onUpdate: (id: number, patch: Partial<FinancialRow>) => void;
  onDelete: (id: number) => void;
};

const FinancialRowItem = memo(function FinancialRowItem({
  row,
  editMode,
  canDelete,
  parseDataOnly,
  showIfaColumn,
  commodityOptions,
  onCreateCommodity,
  onUpdate,
  onDelete,
}: FinancialRowItemProps) {
  const [commodityInput, setCommodityInput] = useState('');
  const [creatingCommodity, setCreatingCommodity] = useState(false);
  const isParseRow = isParseSourcedFinancialRow(row);
  const isReadOnly = parseDataOnly || isParseRow || !editMode;

  useEffect(() => {
    const label =
      row.commodity?.trim() ||
      (row.commodityID != null && row.commodityID > 0
        ? commodityOptions.find((option) => option.commodityID === row.commodityID)?.commodity?.trim() ?? ''
        : '');
    setCommodityInput(label);
  }, [row.id, row.commodity, row.commodityID, commodityOptions]);

  const commodityValue = getCommodityValue(row, commodityOptions);

  const applyCommoditySelection = useCallback(
    async (newValue: CommodityOption | string | null) => {
      if (!newValue) {
        onUpdate(row.id, { commodityID: null, commodity: '' });
        return;
      }

      if (typeof newValue === 'string') {
        const trimmed = newValue.trim();
        if (!trimmed) {
          onUpdate(row.id, { commodityID: null, commodity: '' });
          return;
        }

        const existing = commodityOptions.find(
          (option) => option.commodity?.trim().toLowerCase() === trimmed.toLowerCase()
        );
        if (existing) {
          onUpdate(row.id, {
            commodityID: existing.commodityID ?? null,
            commodity: existing.commodity ?? trimmed,
          });
          return;
        }

        setCreatingCommodity(true);
        try {
          const created = await onCreateCommodity(trimmed);
          if (created) {
            onUpdate(row.id, {
              commodityID: created.commodityID ?? null,
              commodity: created.commodity ?? trimmed,
            });
          } else {
            onUpdate(row.id, { commodityID: null, commodity: trimmed });
          }
        } finally {
          setCreatingCommodity(false);
        }
        return;
      }

      onUpdate(row.id, {
        commodityID: newValue.commodityID ?? null,
        commodity: newValue.commodity ?? '',
      });
    },
    [commodityOptions, onCreateCommodity, onUpdate, row.id]
  );

  const handleCreateNewCommodity = useCallback(async () => {
    const trimmed = commodityInput.trim();
    if (!trimmed) return;
    await applyCommoditySelection(trimmed);
    setCommodityInput('');
  }, [applyCommoditySelection, commodityInput]);

  const canCreateNewCommodity =
    editMode &&
    !isReadOnly &&
    commodityInput.trim().length > 0 &&
    !commodityOptions.some(
      (option) => option.commodity?.trim().toLowerCase() === commodityInput.trim().toLowerCase()
    );

  const commodityLabel =
    row.commodity?.trim() ||
    (row.commodityID != null && row.commodityID > 0
      ? commodityOptions.find((option) => option.commodityID === row.commodityID)?.commodity?.trim()
      : '') ||
    '—';

  return (
    <TableRow hover sx={isParseRow ? { bgcolor: '#fafafa' } : undefined}>
      {showIfaColumn && (
        <TableCell sx={{ py: 0.5, px: 0.75 }}>
          <Typography variant="body2" sx={{ ...readOnlyCellSx, fontFamily: 'monospace', fontSize: 12 }}>
            {row.supplierIFA?.trim() || '—'}
          </Typography>
        </TableCell>
      )}
      <TableCell sx={{ py: 0.5, px: 0.75 }}>
        {isReadOnly ? (
          <Typography variant="body2" sx={readOnlyCellSx}>{row.fy || '—'}</Typography>
        ) : (
          <TextField
            value={row.fy ?? ''}
            size="small"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': { height: 32, bgcolor: '#fff' },
              '& .MuiOutlinedInput-input': { py: 0, px: 1, fontSize: 13 },
            }}
            onChange={(e) => onUpdate(row.id, { fy: e.target.value })}
          />
        )}
      </TableCell>
      <TableCell sx={{ py: 0.5, px: 0.75 }}>
        {isReadOnly ? (
          <Typography variant="body2" sx={readOnlyCellSx}>{row.period || '—'}</Typography>
        ) : (
          <TextField
            value={row.period ?? ''}
            size="small"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': { height: 32, bgcolor: '#fff' },
              '& .MuiOutlinedInput-input': { py: 0, px: 1, fontSize: 13 },
            }}
            onChange={(e) => onUpdate(row.id, { period: e.target.value })}
          />
        )}
      </TableCell>
      <TableCell sx={{ py: 0.5, px: 0.75 }}>
        {isReadOnly ? (
          <Typography variant="body2" sx={readOnlyCellSx}>{row.value || '—'}</Typography>
        ) : (
          <TextField
            value={row.value ?? ''}
            size="small"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': { height: 32, bgcolor: '#fff' },
              '& .MuiOutlinedInput-input': { py: 0, px: 1, fontSize: 13 },
            }}
            onChange={(e) => onUpdate(row.id, { value: e.target.value })}
          />
        )}
      </TableCell>
      <TableCell sx={{ py: 0.5, px: 0.75 }}>
        {isReadOnly ? (
          <Typography variant="body2" sx={readOnlyCellSx}>{commodityLabel}</Typography>
        ) : editMode ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Autocomplete
              freeSolo
              options={commodityOptions}
              getOptionLabel={getCommodityLabel}
              isOptionEqualToValue={isSameCommodityOption}
              filterSelectedOptions
              value={commodityValue}
              inputValue={commodityInput}
              onInputChange={(_e, newInput) => setCommodityInput(newInput)}
              onChange={(_e, newValue) => {
                void applyCommoditySelection(newValue);
              }}
              fullWidth
              disabled={creatingCommodity}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Commodity"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': { height: 32, bgcolor: '#fff' },
                    '& .MuiOutlinedInput-input': { py: 0, px: 1, fontSize: 13 },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {creatingCommodity ? <CircularProgress color="inherit" size={14} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <SecondaryButton
              size="small"
              title="Create a new commodity"
              onClick={() => {
                void handleCreateNewCommodity();
              }}
              disabled={!canCreateNewCommodity || creatingCommodity}
              sx={{
                minWidth: 44,
                px: 1,
                height: 32,
                fontSize: 11,
                whiteSpace: 'nowrap',
              }}
            >
              New
            </SecondaryButton>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ fontSize: 13, px: 0.5 }}>
            {commodityLabel}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ py: 0.5, px: 0, textAlign: 'center' }}>
        <Checkbox
          size="small"
          checked={Boolean(row.pov)}
          disabled={isReadOnly}
          sx={{ p: 0.5 }}
          onChange={(e) => onUpdate(row.id, { pov: e.target.checked })}
        />
      </TableCell>
      <TableCell sx={{ py: 0.5, px: 0, textAlign: 'center' }}>
        <Checkbox
          size="small"
          checked={Boolean(row.pvo)}
          disabled={isReadOnly}
          sx={{ p: 0.5 }}
          onChange={(e) => onUpdate(row.id, { pvo: e.target.checked })}
        />
      </TableCell>
      <TableCell sx={{ py: 0.5, px: 0, textAlign: 'center' }}>
        {editMode && canDelete && !isReadOnly && (
          <DeleteIconButton label="Remove row" onClick={() => onDelete(row.id)} />
        )}
      </TableCell>
    </TableRow>
  );
});

export type FinancialDetailsTableProps = {
  rows: FinancialRow[];
  onRowsChange: React.Dispatch<React.SetStateAction<FinancialRow[]>>;
  editMode: boolean;
  parseDataOnly?: boolean;
  filterResetKey?: number;
  commodityOptions: CommodityOption[];
  onCreateCommodity: (name: string) => Promise<CommodityOption | null>;
};

export const FinancialDetailsTable = memo(function FinancialDetailsTable({
  rows,
  onRowsChange,
  editMode,
  parseDataOnly = true,
  filterResetKey = 0,
  commodityOptions,
  onCreateCommodity,
}: FinancialDetailsTableProps) {
  const [financialYearFilter, setFinancialYearFilter] = useState('ALL');
  const [financialTypeFilter, setFinancialTypeFilter] = useState<'ALL' | 'POV' | 'PVO'>('ALL');
  const [isFilterPending, startFilterTransition] = useTransition();

  const hasParseRows = useMemo(() => rows.some(isParseSourcedFinancialRow), [rows]);
  const showIfaColumn = hasParseRows;

  useEffect(() => {
    setFinancialYearFilter('ALL');
    setFinancialTypeFilter('ALL');
  }, [filterResetKey]);

  const financialYearFilterOptions = useMemo(() => {
    const years = Array.from(
      new Set(rows.map((row) => String(row?.fy ?? '').trim()).filter((fy) => fy.length > 0))
    );
    return years.sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const fy = String(row?.fy ?? '').trim();
      const yearMatch = financialYearFilter === 'ALL' || fy === financialYearFilter;
      const typeMatch =
        financialTypeFilter === 'ALL' ||
        (financialTypeFilter === 'POV' && Boolean(row?.pov)) ||
        (financialTypeFilter === 'PVO' && Boolean(row?.pvo));
      return yearMatch && typeMatch;
    });
  }, [rows, financialYearFilter, financialTypeFilter]);

  const updateRow = useCallback(
    (id: number, patch: Partial<FinancialRow>) => {
      onRowsChange((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    },
    [onRowsChange]
  );

  const handleDeleteRow = useCallback(
    (id: number) => {
      onRowsChange((prev) => {
        const next = prev.filter((row) => row.id !== id);
        return next.length > 0 ? next : [];
      });
    },
    [onRowsChange]
  );

  const handleAddRow = useCallback(() => {
    onRowsChange((prev) => [...prev, createEmptyFinancialRow()]);
  }, [onRowsChange]);

  const filterFieldSx = {
    bgcolor: '#fff',
    opacity: isFilterPending ? 0.7 : 1,
    transition: 'opacity 0.15s ease',
    '& .MuiOutlinedInput-root': { height: 40 },
    '& .MuiOutlinedInput-input': { py: 0, fontSize: 13 },
    '& .MuiInputLabel-root': { fontSize: 13 },
  };

  const emptyColSpan = showIfaColumn ? 8 : 7;

  return (
    <>
      {parseDataOnly && (
        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#6b7280', fontSize: 12 }}>
          Financial details are sourced from Parse Data and cannot be edited manually. Use Parse Data import to load or refresh rows.
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#6b7280', minWidth: 56 }}>
          <FilterListIcon sx={{ fontSize: 18 }} />
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Filters
          </Typography>
          {isFilterPending ? <CircularProgress size={12} sx={{ ml: 0.5 }} /> : null}
        </Box>
        <Box sx={{ flex: '1 1 140px', minWidth: 130 }}>
          <TextField
            select
            label="Financial Year"
            size="small"
            fullWidth
            value={financialYearFilter}
            onChange={(e) => {
              startFilterTransition(() => setFinancialYearFilter(e.target.value));
            }}
            sx={filterFieldSx}
          >
            <MenuItem value="ALL">All Years</MenuItem>
            {financialYearFilterOptions.map((fy) => (
              <MenuItem key={fy} value={fy}>
                {fy}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box sx={{ flex: '1 1 100px', minWidth: 100 }}>
          <TextField
            select
            label="Type"
            size="small"
            fullWidth
            value={financialTypeFilter}
            onChange={(e) => {
              startFilterTransition(() => setFinancialTypeFilter(e.target.value as 'ALL' | 'POV' | 'PVO'));
            }}
            sx={filterFieldSx}
          >
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="POV">POV</MenuItem>
            <MenuItem value="PVO">PVO</MenuItem>
          </TextField>
        </Box>
      </Box>

      <TableContainer sx={{ border: '1px solid #e5e7eb', borderRadius: 1, bgcolor: '#fff' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f3f4f6' }}>
              {showIfaColumn && (
                <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.75, width: '10%' }}>IFA</TableCell>
              )}
              <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.75, width: '14%' }}>Fiscal Year</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.75, width: '10%' }}>Period</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.75, width: '16%' }}>Value (€)</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.75 }}>Commodity</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.75, width: 52, textAlign: 'center', px: 0.5 }}>POV</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.75, width: 52, textAlign: 'center', px: 0.5 }}>PVO</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.75, width: 40, px: 0.5 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((row) => (
              <FinancialRowItem
                key={row.id}
                row={row}
                editMode={editMode}
                parseDataOnly={parseDataOnly}
                showIfaColumn={showIfaColumn}
                canDelete={rows.length > 1}
                commodityOptions={commodityOptions}
                onCreateCommodity={onCreateCommodity}
                onUpdate={updateRow}
                onDelete={handleDeleteRow}
              />
            ))}
            {filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={emptyColSpan} sx={{ py: 2, fontSize: 13, color: 'text.secondary', textAlign: 'center' }}>
                  {rows.length === 0
                    ? 'No financial details yet. Select Parse Data suppliers above and click Import.'
                    : 'No financial rows match the selected filters.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {editMode && !parseDataOnly && (
        <SecondaryButton
          size="small"
          onClick={handleAddRow}
          sx={{ mt: 1, fontSize: 13, px: 2, py: 0.75 }}
        >
          + Add row
        </SecondaryButton>
      )}
    </>
  );
});

export { createEmptyFinancialRow };
